/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ A SUGGESTION SOMEBODY HAS PUT AWAY — item 58.                          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *"A suggestion can be dismissed and stays dismissed until used again."*
 *
 * ⚠️⚠️ **THE VALUE IS THE WORKLOG'S ID, AND THAT IS WHAT MAKES *"UNTIL USED AGAIN"* FREE.** A
 * suggestion's identity is `taskId|note` — `time-route.tsx` deduplicates on exactly that pair — and
 * what it OFFERS is the newest worklog carrying it. Recording which worklog was dismissed means
 * logging that task with that note again produces a newer one, whose id differs, and the row comes
 * back with no expiry logic, no timestamps and nothing to get wrong about clocks.
 *
 * ⚠️⚠️ **`localStorage`, NOT THE SERVER, AND THAT IS A REAL LIMIT WORTH STATING.** A dismissal does
 * not follow somebody to another browser or survive clearing site data. It is a convenience over a
 * convenience strip — the alternative is a table, a migration and an endpoint for a preference
 * about six rows — and losing one costs a person one click.
 *
 * ⚠️ **Keyed per workspace.** The same task cannot appear in two, but the recents strip is
 * per-workspace and a shared key would make a dismissal in one silently apply in another.
 */

const PREFIX = 'forecastify.recents.dismissed';

/** ⚠️ `taskId|note` → the worklog id that was on screen when it was dismissed. */
export type DismissedRecents = Readonly<Record<string, string>>;

function keyFor(workspaceId: string): string {
  return `${PREFIX}.${workspaceId}`;
}

/**
 * ⚠️ **Every read is guarded and every failure is an empty map.** `localStorage` throws in a
 * private window and in a browser set to block site data, and a suggestion strip must not be the
 * thing that stops a screen rendering.
 */
export function readDismissed(workspaceId: string | null): DismissedRecents {
  if (workspaceId === null) return {};
  try {
    const raw = globalThis.localStorage.getItem(keyFor(workspaceId));
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};

    /* ⚠️ Filtered to string values: this is data a person can edit in a devtools console, and a
       number where an id belongs would flow into a comparison that silently never matches. */
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value === 'string') out[key] = value;
    }
    return out;
  } catch {
    return {};
  }
}

/** Put one away. ⚠️ Returns the next map, so a caller can set state without re-reading. */
export function dismissRecent(
  workspaceId: string | null,
  current: DismissedRecents,
  suggestionKey: string,
  worklogId: string,
): DismissedRecents {
  const next = { ...current, [suggestionKey]: worklogId };
  if (workspaceId === null) return next;
  try {
    globalThis.localStorage.setItem(keyFor(workspaceId), JSON.stringify(next));
  } catch {
    /* Not stored, so it comes back on the next load. That is the whole cost. */
  }
  return next;
}

/** ⚠️ The same pair `time-route.tsx` deduplicates on — one definition, so the two cannot drift. */
export function suggestionKey(taskId: string, note: string | null): string {
  return `${taskId}|${note ?? ''}`;
}
